import { MeetingSessionOvenMediaReceiverManagerBaseData, useMeetingSessionOvenMediaReceiverManager } from "@kalyzee/kast-web-sdk";
import { ChatRef, useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";
import {
  MeetingSession,
  MeetingSessionMedia,
  MeetingState
} from "@kalyzee/kast-web-sdk/websocket";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SlaveChat from "../../../components/chat/SlaveChat";
import { BaseTopBar } from "../../../components/common/BaseTopBar";
import { PageSlaveMode } from "../../../components/navigation/PageContext";
import PauseView from "../../../components/utils/PauseView";
import { ChatWorkBox } from "../../../components/workBoxes/ChatWorkBox";
import { PlayerGridWorkBox, PlayerGridWorkBoxProps } from "../../../components/workBoxes/GridWorkBox";
import { ScreenConstants } from "../../../constants/screens";
import { ClientContainersIteration } from "../../../helpers/bridge/common";
import { matchEngine } from "../../../helpers/matchEngine";
import { ScreenHelpers } from "../../../helpers/screen";
import { useMeetingSlaveMessagingBridge } from "../../../hooks/secondaryScreens/messagingBridge";
import { useScreen } from "../../../hooks/secondaryScreens/screen";
import { useSettings } from "../../../hooks/settings";
import { SecondaryScreenLayoutVariant } from "../../../interfaces/secondaryScreens";
import styles from "./meeting.module.css";

const MeetingSlavePage = () => {
  const { bridge, meeting, session, sessions, slaveChatAdapter } = useMeetingSlaveMessagingBridge();
  const bridgeRef = useRefAutoUpdate(bridge);

  const { screen, layout } = useScreen();
  const [iteration, setIteration] = useState<ClientContainersIteration>({ containers: [] });

  /**
   * Currently selected layout sub-variant
   * Based on matching conditions and current context (meeting, session, videoContainers)
   */
  const layoutVariant = useMemo(() => {
    return layout.variants?.find(v => {
      if (!v.match) return true;
      return matchEngine(
        { 
          meeting, 
          session,
          videoContainers: iteration.containers,
        },
        v.match,
      );
    });
  }, [layout, meeting, session, iteration.containers]);

  
  const [mediasToPreload, setMediasToPreload] = useState<MeetingSessionMedia[]>([]);

  const { manager: receptionManager, sessions: videoSessions } = useMeetingSessionOvenMediaReceiverManager(
    sessions,
    useCallback(
      (curr: MeetingSession, s: MeetingSessionMedia) => {
        if (mediasToPreload.find((m) => m.id === s.id)) return true;
        return iteration.containers.some(container => container.sessions.some((cs) => cs.id === s.id));
      },
      [iteration, mediasToPreload]
    ),
  );

  const [settings] = useSettings();

  const timeoutDisplaySessionsRef = useRef<NodeJS.Timeout>();

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const clean = () => {
    bridgeRef.current?.postClearClientData(PageSlaveMode.MEETING);
    clearTimeout(timeoutDisplaySessionsRef.current);
  };

  // ---------------------------------------------------------------------- //
  // --------------------------- EFFECTS ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    bridgeRef.current?.postClientData(PageSlaveMode.MEETING, {
      layoutId: layout.id,
    });
  }, [screen, meeting, layout, bridgeRef]);

  useEffect(() => {
    if (meeting?.state === MeetingState.RUNNING) {
      receptionManager.restore();
    } else {
      receptionManager.pause();
    }
  }, [meeting, receptionManager]);

  useEffect(() => {
    if (!bridge) return;
    const displayCarouselListener = bridge.addEventListener("displayCarousel", (msg) => {
      const { data, delay } = msg.content;
      if (delay <= 0) {
        clearTimeout(timeoutDisplaySessionsRef.current);
        setMediasToPreload([]);
        setIteration(data);
      } else {
        setMediasToPreload(data.containers.flatMap(c => c.sessions));
        timeoutDisplaySessionsRef.current = setTimeout(() => {
          setMediasToPreload([]);
          setIteration(data);
        }, delay);
      }
    });
    return () => {
      displayCarouselListener.flush();
    };
  }, [bridge]);

  useEffect(() => {
    return () => {
      clean();
    };
  }, []);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER --------------------------------- //
  // ---------------------------------------------------------------------- //

  const renderVideoColumn = (
    cols: number,
    rows: number,
    render: (children: React.ReactNode) => React.ReactNode,
    container?: ClientContainersIteration["containers"][0],
    boxPreset?: PlayerGridWorkBoxProps["preset"],
  ) => {
    if (!meeting || !session || !container) return null;
    const columnSessions = container.sessions
      .map(media => videoSessions.find(s => s.data.mediaSession.id === media.id))
      .filter(s => !!s);
    if (!columnSessions.length) return null;
    return render(
      <div className={styles.column}>
        <PlayerGridWorkBox
          meeting={meeting}
          sessions={sessions ?? []}
          ownSession={session}
          videoSessions={columnSessions as OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData<any>>[]}
          columns={cols}
          rows={rows}
          preset={boxPreset}
          gap={5}
          totalNumber={container.totalSessions}
        />
        {settings.carousel?.debug && (
          <div className={styles.debugContainer}>
            <div>Carousel ID : {container.carouselId}</div>
            <div>Group ID : {container.groupId}</div>
            <div>Page : {container.page + 1} / {container.pageCount}</div>
          </div>
        )}
        {renderPager(container.page, container.pageCount)}
      </div>
    );
  }

  const renderChatColumn = () => {
    return (
      <ChatWorkBox>
        <SlaveChat
          chatAdapter={slaveChatAdapter}
        />
      </ChatWorkBox>
    );
  }


  const renderLayoutColumns = () => {
    const nodes: React.ReactNode[] = [];
    
    const renderColumn = (
      column: SecondaryScreenLayoutVariant["columns"][0],
      render: (children: React.ReactNode) => React.ReactNode,
    ) => {
      if (column.contentType === "players") {
        const dim = ScreenHelpers.getColumnPlayers(column);
        const container = 
          layout.videoContainers?.find(c => c.id === column.videoContainerId)
          ?? ScreenConstants.DefaultLayout.videoContainers?.[0];
        const containerIteration = iteration.containers.find(c => c.id === container?.id);
        return renderVideoColumn(
          dim.columns,
          dim.rows,
          render,
          containerIteration,
          column.playerBoxPreset,
        );
      } else if (column.contentType === "chat") {
        return render(renderChatColumn());
      } else {
        return render(<>Unimplemented column type</>);
      }
    }

    layoutVariant?.columns.forEach((column, idx) => {
      const _render = (children: React.ReactNode) => {
        const style: React.CSSProperties = {
          height: "100%",
          width: "25%",
          ...(column.style ?? {}),
        };
        return (
          <div style={style} key={`column_${column.contentType === "chat" ? "chat" : `players_${idx}`}`}>
            {children}
          </div>
        );  
      }
      nodes.push(renderColumn(column, _render));
    });

    return (
      <div style={{ 
        height: "100%", 
        width: "100%", 
        display: "flex",
        gap: "1em",
        justifyContent: layoutVariant?.justifyContent,
      }}>
        {nodes}
      </div>
    );
  };

  const renderTopBar = () => {
    const topBarOptions = layoutVariant?.topBar;
    if (!topBarOptions) return null;
    return (
      <BaseTopBar
        showLogo={topBarOptions.brand}
        watchingNumber={topBarOptions.watchers ? sessions?.length : undefined}
        user={topBarOptions.user ? session?.user : undefined}
        meetingName={topBarOptions.meetingName ? meeting?.name : undefined}
        meetingStartedAt={topBarOptions.timer ? meeting?.startedAt : undefined}
      />
    );
  };

  const renderPager = (page: number, pageCount: number) => (
    pageCount > 1 ? (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "20px",
          padding: "10px 10px",
          backgroundColor: "#88888866",
          margin: "10px 0px",
          gap: "5px",
        }}
      >
        {new Array(pageCount).fill(0).map((_, i) => {
          return (
            <div
              key={`page_${i}`}
              style={{ width: "6px", height: "6px", borderRadius: "100%", backgroundColor: i === page ? "#CCCCCCDD" : "#AAAAAA44" }}
            />
          );
        })}
      </div>
    ) : null
  );

  const renderContent = () => {
    if (!meeting || !session) {
      const screenName = screen?.name ?? (screen?.index !== undefined ? `Écran n°${screen.index}` : undefined);
      return (
        <div className={styles.waiting}>
          <div className={styles.content}>En attente d'un événement ...</div>
          {screenName && <div className={styles.footer}>
            {screenName}
          </div>}
        </div>
      );
    }
    return (
      <>
        <div style={{
          display: "flex",
          width: "100%",
          flex: 1,
          minHeight: 0,
        }}>
          {renderLayoutColumns()}
        </div>
      </>
    );
  };

  return (
    <PauseView
      meetingPaused={meeting?.state === MeetingState.PAUSED}
      meetingPausedAt={meeting?.pausedAt}
    >
      <div className={styles.container}>
        {renderTopBar()}
        <div style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          flex: 1,
          minHeight: 0,
          padding: "1em",
        }}>
          {renderContent()}
        </div>
        {settings.carousel?.debug && (
          <div className={styles.debugContainer} style={{ top: "unset", bottom: "10%" }}>
            <div>Preloading Sessions : {mediasToPreload.map(session => session.id).join(", ")}</div>
            <br />
            <div>Variant : {JSON.stringify(layoutVariant)}</div>
            <br />
            <div>Iteration : {JSON.stringify(({
              ...iteration,
              containers: iteration.containers.map(c => ({
                ...c,
                sessions: c.sessions.map(s => s.id),
              })),
            }))}</div>
          </div>
        )}
      </div>
    </PauseView>
  );
};

export default MeetingSlavePage;
